import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import {
  TemplateRichTextEditor,
  type TemplateVariableFormat,
} from '../composites/TemplateRichTextEditor';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

export type { TemplateVariableFormat };

export interface RhfTemplateRichTextProps extends RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  variables?: string[];
  variableFormat?: TemplateVariableFormat;
}

export const RhfTemplateRichText = React.forwardRef<HTMLDivElement, RhfTemplateRichTextProps>(
  function RhfTemplateRichText(
    {
      control,
      name,
      label,
      hint,
      tooltip,
      required = false,
      semanticId,
      disabled = false,
      variables = [],
      variableFormat = 'mustache',
      controlWidth,
    },
    ref,
  ) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });

          return (
            <FormField
              ref={ref}
              label={label}
              hint={hint}
              tooltip={tooltip}
              required={required}
              semanticId={semanticId}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <TemplateRichTextEditor
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={disabled}
                hasError={Boolean(errorText)}
                variables={variables}
                variableFormat={variableFormat}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfTemplateRichText.displayName = 'RhfTemplateRichText';
